import { createContext, useContext } from 'react';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Exposes the app-wide (root) QueryClient to components rendered INSIDE a
 * `ProtectedQueryProvider`, where `useQueryClient()` returns the per-salon
 * client that is discarded on every salon switch.
 *
 * Only user-scoped data that is identical in every salon (e.g. the agency
 * member's own salon list) may live here — never salon data. The root client
 * is cleared on sign-out / actor change (`inner-app.tsx`), which is what makes
 * keeping user-keyed entries across salon switches safe.
 *
 * A context (rather than capturing `useQueryClient()` above the protected
 * provider and prop-drilling it) keeps call sites decoupled from where they
 * sit in the provider tree.
 */
export const RootQueryClientContext = createContext<QueryClient | null>(null);

export function useRootQueryClient(): QueryClient {
  const client = useContext(RootQueryClientContext);
  if (!client) {
    throw new Error('useRootQueryClient must be used within QueryProvider');
  }
  return client;
}
